import React, { useState } from "react";
import WeatherConditions from "./WeatherConditions";
import AirQuality from "./AirQuality";
import { FaWind, FaCloudSun } from "react-icons/fa";
import DOMPurify from 'dompurify';

function FacilityResultItem({
  id,
  name,
  description,
  state,
  expanded,
  selectedFieldNotesId,
  onFieldNotes,
  onLocate,
  onExpand,
  latitude,
  longitude,
  children,
  facility_subtype,
  unit_name,
  photo_thumb,
  photo_link,
  photo_text,
  url,
  RecAreaDirections,
  RecAreaReservationURL,
  RecAreaUseFeeDescription,
  // Vendor-specific props
  vendor_name,
  category,
  rating,
  website,
  formatted_address,
  international_phone_number,
  content
}) {
  const [showWeatherLabel, setShowWeatherLabel] = useState(false);
  const [showWeatherComponent, setShowWeatherComponent] = useState(false);
  const [showAirQualityComponent, setShowAirQualityComponent] = useState(false);
  const [localExpanded, setLocalExpanded] = useState(false);
  
  // Use localExpanded state when onExpand is provided (search results), otherwise use expanded prop (Current Selection)
  const isExpanded = typeof onExpand === 'function' ? localExpanded : expanded;

  // Determine if this is a vendor based on vendor_name property
  // Only consider it a vendor if vendor_name exists AND it's different from a standard name field
  // OR if vendor_name exists and we have vendor-specific fields like category or formatted_address
  const isVendor = vendor_name && (category || formatted_address || website || rating);
  
  const handleConditionsClick = (e) => {
    e.stopPropagation();
    setShowWeatherLabel(true);
  };
  const handleWeatherClick = (e) => {
    e.preventDefault();
    setShowWeatherComponent(show => !show);
  };
  const handleAirQualityClick = (e) => {
    e.preventDefault();
    setShowAirQualityComponent(show => !show);
  };
  return (
    <div
      style={{
        padding: 16,
        cursor: "pointer",
        borderBottom: "1px solid #f1f5f9",
        background: isExpanded ? "#ecfdf5" : undefined,
        borderLeft: isExpanded ? "4px solid #059669" : undefined
      }}>
      <div style={{ display: "flex", flexDirection: "column", minHeight: 80, height: "100%" }}>
        <div style={{
          fontSize: 20,
          fontWeight: 700,
          color: "#059669",
          background: "#f0fdf4",
          padding: "6px 8px",
          borderRadius: 6,
          letterSpacing: "0.5px",
          textShadow: "0 1px 2px rgba(0,0,0,0.04)",
          marginBottom: 4
        }}>{name}</div>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 2 }}>
          <label style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 6, 
            height: 32, 
            cursor: "pointer", 
            fontSize: 12,
            fontWeight: 600,
            padding: "4px 8px",
            borderRadius: 6,
            background: selectedFieldNotesId === id ? "#dcfce7" : "#f8fafc",
            border: selectedFieldNotesId === id ? "1px solid #059669" : "1px solid #e2e8f0",
            color: selectedFieldNotesId === id ? "#059669" : "#475569",
            transition: "all 0.2s ease"
          }}>
            <input
              type="checkbox"
              name="fieldNotesCheckbox"
              checked={selectedFieldNotesId === id}
              onChange={e => {
                e.stopPropagation();
                if (onFieldNotes) {
                  onFieldNotes(id);
                }
              }}
              style={{ accentColor: "#059669", width: 16, height: 16 }}
            />
            {selectedFieldNotesId === id ? "Added to Field Notes ✓" : "Add to Field Notes"}
          </label>
          <button
            onClick={e => { 
              e.stopPropagation(); 
              // Validate coordinates before locating
              if (!latitude || !longitude || latitude === '' || longitude === '' || latitude === 0 || longitude === 0) {
                return;
              }
              // Create facility object from props
              const facilityData = {
                id,
                name,
                description,
                state,
                latitude,
                longitude,
                facility_subtype,
                unit_name,
                photo_thumb
              };
              onLocate(facilityData); 
            }}
            style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #3b82f6", fontSize: 12, background: "white", color: "#3b82f6", fontWeight: 600, height: 32 }}
          >
            Locate
          </button>
          {/* Show expand/collapse button except for Current Selection (when onExpand is not provided) */}
          {typeof onExpand === 'function' && (
            <button
              onClick={e => { 
                e.stopPropagation(); 
                setLocalExpanded(!localExpanded);
              }}
              style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #059669", fontSize: 12, background: isExpanded ? "#059669" : "white", color: isExpanded ? "white" : "#059669", fontWeight: 600, height: 32 }}
              aria-expanded={isExpanded}
            >
              {isExpanded ? "Collapse" : "Expand"}
            </button>
          )}
          {isExpanded && (
            <button
              onClick={handleConditionsClick}
              style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #f59e42", fontSize: 12, background: "white", color: "#f59e42", fontWeight: 600, height: 32, marginLeft: 4 }}
              disabled={showWeatherLabel}
            >
              Conditions
            </button>
          )}
        </div>
        <div style={{ fontSize: 14, color: "#64748b", marginTop: 2 }}>{state}</div>
        {isExpanded && (
          <>
            {description && /<\s*\w+.*?>/.test(description) ? (
              <div style={{ marginTop: 6 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
            ) : (
              <div style={{ fontSize: 13, color: "#64748b", flex: 1, wordBreak: 'break-word', whiteSpace: 'pre-line', marginTop: 6, overflow: 'auto' }}>{description}</div>
            )}
            {/* Improved details formatting */}
            <div style={{ marginTop: 12, fontSize: 13, color: '#334155', background: '#f8fafc', borderRadius: 6, padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          {/* Vendor or Facility Info Section */}
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
            {isVendor ? 'Vendor Info' : 'Facility Info'}
          </div>
          
          {/* Vendor-specific fields */}
          {isVendor && (
            <>
              {category && <div style={{ marginBottom: 6 }}><strong>Category:</strong> {category}</div>}
              {rating && <div style={{ marginBottom: 6 }}><strong>Rating:</strong> {rating}⭐</div>}
              {formatted_address && <div style={{ marginBottom: 6 }}><strong>Address:</strong> {formatted_address}</div>}
              {international_phone_number && <div style={{ marginBottom: 6 }}><strong>Phone:</strong> {international_phone_number}</div>}
              {content && <div style={{ marginBottom: 6, marginTop: 8 }}><strong>Details:</strong> <div style={{ marginTop: 4, padding: 8, background: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0' }}>{content}</div></div>}
            </>
          )}
          
          {/* Standard facility fields */}
          {!isVendor && (
            <>
              {facility_subtype && <div style={{ marginBottom: 6 }}><strong>Subtype:</strong> {facility_subtype}</div>}
              {unit_name && <div style={{ marginBottom: 6 }}><strong>Unit Name:</strong> {unit_name}</div>}
            </>
          )}
          
          {state && <div style={{ marginBottom: 6 }}><strong>State:</strong> {state}</div>}

          {/* Location Section */}
          <div style={{ fontWeight: 600, fontSize: 15, margin: '14px 0 8px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>Location</div>
          {latitude !== undefined && latitude !== null && (
            <div style={{ marginBottom: 6 }}>
              <strong>Latitude:</strong> {latitude}
            </div>
          )}
          {longitude !== undefined && longitude !== null && (
            <div style={{ marginBottom: 6 }}>
              <strong>Longitude:</strong> {longitude}
            </div>
          )}

          {/* Photos Section */}
          {(photo_thumb || photo_link || photo_text) && (
            <>
              <div style={{ fontWeight: 600, fontSize: 15, margin: '14px 0 8px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>Photos</div>
              {photo_thumb && (
                <div style={{ margin: '8px 0' }}>
                  <strong>Thumbnail:</strong><br />
                  <img src={photo_thumb} alt="Thumbnail" style={{ maxWidth: 120, borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }} />
                </div>
              )}
              {photo_link && (
                <div style={{ margin: '8px 0' }}>
                  <strong>Photo Link:</strong> <a href={photo_link} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: 13, textDecoration: 'underline' }}>View Photo</a>
                </div>
              )}
              {photo_text && <div style={{ marginBottom: 6 }}><strong>Photo Text:</strong> {photo_text}</div>}
            </>
          )}

          {/* Links Section */}
          {(url || website) && (
            <>
              <div style={{ fontWeight: 600, fontSize: 15, margin: '14px 0 8px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>Links</div>
              {url && (
                <div style={{ marginBottom: 6 }}>
                  <span role="img" aria-label="link" style={{ marginRight: 4 }}>🔗</span>
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: 13, textDecoration: 'underline' }}>Web Link</a>
                </div>
              )}
              {website && isVendor && (
                <div style={{ marginBottom: 6 }}>
                  <span role="img" aria-label="website" style={{ marginRight: 4 }}>🌐</span>
                  <a href={website} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: 13, textDecoration: 'underline' }}>Visit Website</a>
                </div>
              )}
            </>
          )}

          {/* Directions & Fees Section */}
          {(RecAreaDirections || RecAreaReservationURL || RecAreaUseFeeDescription) && (
            <>
              <div style={{ fontWeight: 600, fontSize: 15, margin: '14px 0 8px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>Directions & Fees</div>
              {RecAreaDirections && (
                <div style={{ margin: '8px 0' }}>
                  <strong>Directions:</strong><br />
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(RecAreaDirections) }} />
                </div>
              )}
              {RecAreaReservationURL && (
                <div style={{ margin: '8px 0' }}>
                  <strong>Reservation URL:</strong> <a href={RecAreaReservationURL} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>{RecAreaReservationURL}</a>
                </div>
              )}
              {RecAreaUseFeeDescription && (
                <div style={{ margin: '8px 0' }}>
                  <strong>Use Fee Description:</strong><br />
                  {RecAreaUseFeeDescription}
                </div>
              )}
            </>
          )}

          {/* Show weather/air quality toggles only after Conditions is clicked */}
          {showWeatherLabel && (
            <>
              <div style={{ fontWeight: 600, fontSize: 15, margin: '14px 0 8px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>Conditions</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FaCloudSun style={{ fontSize: 18, color: '#2563eb' }} />
                <a
                  href="#weather"
                  onClick={handleWeatherClick}
                  style={{ fontWeight: 600, color: '#2563eb', textDecoration: 'underline', fontSize: 14, cursor: 'pointer' }}
                >
                  Weather Forecast
                </a>
              </div>
              {showWeatherComponent && latitude != null && longitude != null && (
                <WeatherConditions latitude={latitude} longitude={longitude} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FaWind style={{ fontSize: 18, color: '#059669' }} />
                <a
                  href="#airquality"
                  onClick={handleAirQualityClick}
                  style={{ fontWeight: 600, color: '#059669', textDecoration: 'underline', fontSize: 14, cursor: 'pointer' }}
                >
                  Air Quality
                </a>
              </div>
              {showAirQualityComponent && latitude != null && longitude != null && (
                <AirQuality latitude={latitude} longitude={longitude} />
              )}
            </>
          )}
        </div>
            </>
        )}
      </div>
    </div>
  );
}

export default FacilityResultItem;
